import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Search
} from 'lucide-react';

const Interactions = () => {
  const [searchParams] = useSearchParams();
  const [interactions, setInteractions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(searchParams.get('company') || '');

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const selectedCompanyId = watch('company_id');

  useEffect(() => {
    fetchData();
    
    // If company is provided in URL, automatically show the form
    if (searchParams.get('company')) {
      setShowForm(true);
      setValue('company_id', searchParams.get('company'));
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchContacts(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [interactionsRes, companiesRes] = await Promise.all([
        api.get('/api/interactions'),
        api.get('/api/companies?limit=100')
      ]);
      
      setInteractions(interactionsRes.data.interactions || []);
      setCompanies(companiesRes.data.companies || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load interactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (companyId) => {
    try {
      const response = await api.get(`/api/contacts/company/${companyId}`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const onSubmit = async (data) => {
    console.log('=== FORM SUBMISSION START ===');
    console.log('Form data:', data);
    
    // Add a small delay to see if the function completes
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      
      // Transform data to match backend API
      const interactionData = {
        company_id: parseInt(data.company_id),
        contact_id: data.contact_id ? parseInt(data.contact_id) : null,
        type: data.type,
        method: data.type, // Use type as method for now
        subject: data.notes,
        content: data.notes,
        outcome: 'Completed',
        follow_up_date: data.date
      };

      console.log('Interaction data to send:', interactionData);

      console.log('Making API call...');
      
      if (editingInteraction) {
        console.log('Updating interaction...');
        const response = await api.put(`/api/interactions/${editingInteraction.id}`, interactionData);
        console.log('Update response:', response);
        toast.success('Interaction updated successfully!');
      } else {
        console.log('Creating interaction...');
        const response = await api.post('/api/interactions', interactionData);
        console.log('Create response:', response);
        toast.success('Interaction created successfully!');
      }
      
      console.log('API call successful, resetting form...');
      reset();
      setShowForm(false);
      setEditingInteraction(null);
      fetchData();
      console.log('Form reset complete');
      console.log('=== FORM SUBMISSION END ===');
    } catch (error) {
      console.error('Error in onSubmit:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        console.log('Unauthorized - redirecting to login');
        // Don't redirect here, let the axios interceptor handle it
      } else {
        const message = error.response?.data?.error || 'Failed to save interaction';
        toast.error(message);
      }
      console.log('=== FORM SUBMISSION ERROR END ===');
    }
  };

  const handleEdit = (interaction) => {
    setEditingInteraction(interaction);
    setValue('company_id', interaction.company_id);
    setValue('contact_id', interaction.contact_id);
    setValue('type', interaction.type);
    setValue('notes', interaction.notes);
    setValue('date', interaction.date ? new Date(interaction.date).toISOString().split('T')[0] : '');
    setShowForm(true);
  };

  const handleDelete = async (interactionId) => {
    if (!window.confirm('Are you sure you want to delete this interaction?')) return;
    
    try {
      await api.delete(`/api/interactions/${interactionId}`);
      toast.success('Interaction deleted successfully!');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete interaction';
      toast.error(message);
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="h-5 w-5 text-blue-500" />;
      case 'phone':
        return <Phone className="h-5 w-5 text-green-500" />;
      case 'meeting':
        return <Calendar className="h-5 w-5 text-purple-500" />;
      default:
        return <MessageSquare className="h-5 w-5 text-gray-500" />;
    }
  };

  const getInteractionColor = (type) => {
    switch (type) {
      case 'email':
        return 'bg-blue-100 text-blue-800';
      case 'phone':
        return 'bg-green-100 text-green-800';
      case 'meeting':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInteractions = interactions.filter(interaction => {
    const matchesSearch = (interaction.subject || interaction.content)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         interaction.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         `${interaction.first_name} ${interaction.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || interaction.type === filterType;
    const matchesCompany = !selectedCompany || interaction.company_id === parseInt(selectedCompany);
    
    return matchesSearch && matchesType && matchesCompany;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Interactions</h1>
        <p className="page-subtitle">Track your company interactions and communications</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search interactions..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="meeting">Meeting</option>
            </select>

            <select
              className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => {
            setShowForm(true);
            setEditingInteraction(null);
            reset();
          }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Interaction
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingInteraction ? 'Edit Interaction' : 'Add New Interaction'}
            </h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Company</label>
                  <select
                    {...register('company_id', { required: 'Company is required' })}
                    className="form-select"
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  {errors.company_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.company_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Contact (Optional)</label>
                  <select
                    {...register('contact_id')}
                    className="form-select"
                    disabled={!selectedCompanyId}
                  >
                    <option value="">Select Contact (Optional)</option>
                    {contacts.map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Type</label>
                  <select
                    {...register('type', { required: 'Type is required' })}
                    className="form-select"
                  >
                    <option value="">Select Type</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="meeting">Meeting</option>
                  </select>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    {...register('date', { required: 'Date is required' })}
                    className="form-input"
                  />
                  {errors.date && (
                    <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  {...register('notes', { required: 'Notes are required' })}
                  className="form-textarea"
                  rows="4"
                  placeholder="Enter interaction details..."
                />
                {errors.notes && (
                  <p className="text-red-500 text-sm mt-1">{errors.notes.message}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button type="submit" className="btn btn-primary">
                  {editingInteraction ? 'Update' : 'Create'} Interaction
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingInteraction(null);
                    reset();
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Interactions List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Interactions ({filteredInteractions.length})
          </h3>
        </div>
        <div className="card-body">
          {filteredInteractions.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No interactions found</h3>
              <p className="text-gray-500">Start by adding your first interaction.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInteractions.map((interaction) => (
                <div key={interaction.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getInteractionIcon(interaction.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getInteractionColor(interaction.type)}`}>
                          {interaction.type}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(interaction.date).toLocaleDateString()}
                        </span>
                      </div>
                                             <h4 className="font-semibold text-gray-900 mb-1">
                         {interaction.company_name} - {interaction.first_name} {interaction.last_name}
                       </h4>
                       <p className="text-gray-600 text-sm">{interaction.subject || interaction.content}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(interaction)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(interaction.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Interactions;
